import React from 'react';
import { injectIntl, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import { find, isEmpty, get } from 'lodash';
import EnvApi from '../../../api/api-env';

const siftActivities = (activities, input) => {
  const { Sifter } = window;
  return new Promise((resolve, reject) => {
    const { items } = new Sifter(activities).search(input, {
      fields: ['dropdown', 'use-subtype', 'display_use_type'],
      limit: 5
    });
    if (!isEmpty(items)) {
      const mapSiftedActivities = items.map((item) => ({
        label: activities[item.id].dropdown,
        value: activities[item.id]
      }));
      return resolve(mapSiftedActivities);
    }
    return reject();
  });
};

const withActivityMobileTray = (Component) => {
  class HOC extends React.Component {
    constructor (props) {
      super(props);

      const value = get(this.props, 'input.value.activity', '');
      this.state = {
        isFocused: false,
        defaultOptions: [],
        activitiesList: [],
        isOptionError: false,
        inputDisplay: value
      };

      this.blur = this.blur.bind(this);
      this.loadOptions = this.loadOptions.bind(this);
      this.onChange = this.onChange.bind(this);
      this.inputChange = this.inputChange.bind(this);
      this.handleInputFocus = this.handleInputFocus.bind(this);
      this.handleInputBlur = this.handleInputBlur.bind(this);
      this.handleContainerFocus = this.handleContainerFocus.bind(this);
      this.focus = this.focus.bind(this);
      this.validate = this.validate.bind(this);
    }

    componentDidMount () {
      const { intl } = this.props;
      EnvApi.getActivities(intl.locale, (response) => {
        if (200 === response.statusCode) {
          this.setState({
            activitiesList: response.response
          });
        }
      });
    }

    componentDidUpdate (prevProps) {
      const { defaultActivity, setValidator, activeInput } = this.props;
      const value = get(this.props, 'input.value.activity', '');
      const setValue = get(this.props, 'input.onChange', null);
      const prevValue = get(prevProps, 'input.value.activity', '');

      const { activitiesList, isFocused } = this.state;

      if (defaultActivity && activitiesList.length) {
        (async () => {
          try {
            // if a defaultActivity was provided, find it in the activities list
            // and set the initial value for the input
            const siftedActivities = await siftActivities(
              activitiesList,
              defaultActivity
            );
            const exactMatch = find(siftedActivities, [
              'value.dropdown',
              defaultActivity
            ]);
            if (exactMatch) {
              setValue({ activity: exactMatch }, false);
              this.setState({
                defaultOptions: siftedActivities
              });
            }
          } catch (error) {
            console.error('ActivityInput: error sifting activities', error);
            setValue({ activity: '' }, false);
            this.setState({
              setDefaultOptions: []
            });
          }
        })();
      }

      if (value !== prevValue) {
        this.setState({
          inputDisplay: value
        });
      }

      if ('function' === typeof setValidator) {
        setValidator(this.validate);
      }

      if ('activity' === activeInput && !isFocused) {
        this.focus();
      }
    }

    componentWillUnmount () {
      this.blur();
    }

    blur () {
      const { setActiveInput } = this.props;
      this.setState({
        isFocused: false
      });
      if ('function' === typeof setActiveInput) {
        setActiveInput('');
      }
    }

    loadOptions (inputValue, callback) {
      const { activitiesList } = this.state;
      setTimeout(async () => {
        try {
          const siftedActivities = await siftActivities(
            activitiesList,
            inputValue
          );
          this.setState({
            isOptionError: false
          });
          callback(siftedActivities);
        } catch (e) {
          this.setState({
            isOptionError: true
          });
        }
      }, 0);
    }

    onChange (value, { action }, shouldBlur = true) {
      const setValue = get(this.props, 'input.onChange', null);
      const { activitiesList } = this.state;
      if (!value && 'clear' === action) {
        this.setState({
          defaultOptions: []
        });
        setValue({ activity: '' });
      }

      if (value && 'select-option' === action) {
        (async () => {
          const siftedActivities = await siftActivities(
            activitiesList,
            value.label
          );
          if (shouldBlur) {
            this.blur();
          }
          this.setState({
            defaultOptions: siftedActivities
          });
        })();
      }
      if (setValue) {
        setValue({ activity: get(value, 'value.dropdown', '') });
      }
    }

    inputChange (value, { action }) {
      const setValue = get(this.props, 'input.onChange', null);

      if (!value && 'input-change' === action) {
        this.setState({
          defaultOptions: []
        });
        setValue({ activity: '' });
      }
      if ('input-change' === action) {
        this.setState({
          inputDisplay: value || ''
        });
      }
    }

    handleInputFocus (e) {
      const { setActiveInput } = this.props;
      if (e) {
        e.preventDefault();
        e.target.setSelectionRange(0, e.target.value.length);
      }
      this.setState({
        isOptionError: false,
        isFocused: true
      });
      if ('function' === typeof setActiveInput) {
        setActiveInput('activity');
      }
    }

    handleInputBlur (e) {
      if (e) {
        e.preventDefault();
      }
      this.blur();
    }

    handleContainerFocus () {
      const { setActiveInput } = this.props;
      this.setState({
        isFocused: true
      });
      if ('function' === typeof setActiveInput) {
        setActiveInput('activity');
      }
    }

    focus () {
      this.handleContainerFocus();
      this.handleInputFocus();
    }

    validate () {
      const value = get(this.props, 'input.value', {});
      let isValid = true;
      const hasValue = !(isEmpty(value) || isEmpty(value.value));
      if (!hasValue) {
        isValid = false;
        this.focus();
      }
      return isValid;
    }

    render () {
      const { defaultOptions, isOptionError, activitiesList, isFocused, inputDisplay } = this.state;
      const displayValue = get(this.props, 'input.value.activity', '');
      return (
        <Component
          loadOptions={this.loadOptions}
          onChange={this.onChange}
          inputChange={this.inputChange}
          handleInputFocus={this.handleInputFocus}
          handleInputBlur={this.handleInputBlur}
          handleContainerFocus={this.handleContainerFocus}
          focus={this.focus}
          blur={this.blur}
          validate={this.validate}
          defaultOptions={defaultOptions}
          isOptionError={isOptionError}
          activitiesList={activitiesList}
          isFocused={isFocused}
          inputDisplay={inputDisplay}
          displayValue={displayValue}
          {...this.props}
        />
      );
    }
  }
  HOC.propTypes = {
    intl: intlShape,
    defaultActivity: PropTypes.object,
    setValidator: PropTypes.func,
    activeInput: PropTypes.string,
    setActiveInput: PropTypes.func
  };
  return injectIntl(HOC);
};

export default withActivityMobileTray;
